var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import styled from 'styled-components';
import { MathMinus } from 'styled-cssgg';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import useOnClickOutside from '../hooks/use-click-outside';
import { PopupContext, defaultPopupConfig, usePopup } from './popup-context';
import { borderRadius } from '../theme/border-radius';
import { shadow } from '../theme/shadow';
import { CloseIcon } from '../close-icon/close-icon';
const Wrapper = styled.div ``;
const Main = styled(animated.div) `
  background-color: var(--bg-color);
  z-index: ${(props) => props.zIndex};
  box-shadow: ${shadow.lg};
  border-radius: ${borderRadius.lg} ${borderRadius.lg} 0 0;
  position: fixed;
  bottom: 0px;
  width: 100%;
  min-height: 200px;
  .popup-handler {
    display: grid;
    padding: 12px;
    [data-role='popup-handler-line'] {
      margin: -12px;
      padding: 12px;
      display: inline-flex;
      justify-self: center;
      cursor: pointer;
      color: ${() => 'var(--text-color-0)'};
      border-radius: 100%;
    }
  }
`;
const Mask = styled(animated.div) `
  position: fixed;
  left: 0;
  bottom: 0;
  right: 0;
  top: 0;
  opacity: 0.5;
  background-color: rgba(0, 0, 0, 0.65);
`;
let id = 0;
const zIndex = 1000;
const StyledCloseIcon = styled(CloseIcon) `
  margin: auto 0px;
  display: flex;
  justify-self: end;
`;
export const Popup = (_a) => {
    var { showClose = true } = _a, props = __rest(_a, ["showClose"]);
    const [toggle, setToggle] = useState(false);
    id += 1;
    const idRef = useRef(id);
    const controlledVisible = props.visible || toggle;
    const popupRef = useRef(null);
    const config = usePopup({ id: idRef });
    const hasPopupChildVisible = useMemo(() => {
        return Object.keys(config.popups).some((k) => {
            console.log(k, idRef.current, config.popups[idRef.current], Number(idRef.current) > Number(k), Number(idRef.current) > Number(k) && config.popups[idRef.current] === 1);
            // child popup id must large than parent
            if (Number(idRef.current) < Number(k) && config.popups[k] === 1) {
                return true;
            }
            return false;
        });
    }, [config.popups, idRef]);
    const styles = useSpring({
        transform: controlledVisible
            ? hasPopupChildVisible
                ? 'translateY(-20%)'
                : 'translateY(0%)'
            : 'translateY(100%)',
        scale: controlledVisible && hasPopupChildVisible ? 0.8 : 1,
    });
    useOnClickOutside(popupRef, () => {
        var _a;
        (_a = props.onClickOutside) === null || _a === void 0 ? void 0 : _a.call(props);
        setToggle(false);
    }, {});
    useEffect(() => { }, [controlledVisible, config.popups]);
    useEffect(() => {
        var _a;
        (_a = config.onTogglePopupChildVisible) === null || _a === void 0 ? void 0 : _a.call(config, idRef.current, controlledVisible ? 1 : 0);
    }, [controlledVisible, idRef, config.onTogglePopupChildVisible]);
    const ids = Object.keys(config.popups);
    return createPortal(React.createElement(Wrapper, { className: cx('popup', props.className), "data-role": "popup" },
        React.createElement(Mask, { className: "popup-mask", "data-role": "popup-mask", style: {
                pointerEvents: controlledVisible ? undefined : 'none',
                zIndex: controlledVisible ? 999 : -1,
            } }),
        React.createElement(Main, { zIndex: config.len > 1 ? zIndex + ids.findIndex((id) => Number(id) === idRef.current) : zIndex, ref: popupRef, onClick: () => {
                var _a;
                (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props);
                setToggle(false);
            }, style: styles },
            React.createElement("div", { className: "popup-handler", "data-role": "popup-handler" },
                showClose && (React.createElement(StyledCloseIcon, { onClick: () => {
                        var _a;
                        setToggle(false);
                        (_a = props.onClose) === null || _a === void 0 ? void 0 : _a.call(props);
                    } })),
                props.showLine && (React.createElement("p", { className: "popup-handler-line", "data-role": "popup-handler-line", onClick: () => {
                        var _a;
                        setToggle(false);
                        (_a = props.onClose) === null || _a === void 0 ? void 0 : _a.call(props);
                    } },
                    React.createElement(MathMinus, null)))),
            props.children)), document.body);
};
export const PopupGroup = (props) => {
    const [len, setLen] = useState(defaultPopupConfig.len);
    const [popups, setPopups] = useState(defaultPopupConfig.popups);
    const handleRegister = useCallback((id) => {
        setLen((prev) => prev + 1);
        setPopups((prev) => (Object.assign(Object.assign({}, prev), { [id]: -1 })));
    }, []);
    const handleUnRegister = useCallback((id) => {
        setLen((prev) => prev - 1);
        setPopups((prev) => (Object.assign(Object.assign({}, prev), { [id]: undefined })));
    }, []);
    const handleToggleVisible = useCallback((id, visible) => {
        setPopups((prev) => (Object.assign(Object.assign({}, prev), { [id]: visible })));
    }, []);
    return (React.createElement(PopupContext.Provider, { value: {
            len,
            popups,
            onRegister: handleRegister,
            onUnRegister: handleUnRegister,
            onTogglePopupChildVisible: handleToggleVisible,
        } }, props.children));
};
